import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileBadge2, Shield, CheckCircle, Clock, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCustomer } from "@/contexts/CustomerContext";
import { useToast } from "@/hooks/use-toast";

export default function KycPage() {
  const navigate = useNavigate();
  const { kycStatus, kycApproved, fetchData } = useCustomer();
  const { toast } = useToast();

  const [documentType, setDocumentType] = useState("");
  const [frontFile, setFrontFile] = useState<File | null>(null);
  const [backFile, setBackFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!documentType || !frontFile) {
      toast({ title: "Missing documents", description: "Select a document type and upload the front image.", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      const { platformApi } = await import("@/platform/api");
      await platformApi.submitKyc(documentType, frontFile, backFile ?? undefined);

      toast({ title: "KYC submitted", description: "Your documents are now in review." });
      await fetchData();
    } catch (error) {
      toast({
        title: "Submission failed",
        description: error instanceof Error ? error.message : "Could not submit KYC documents.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Already approved
  if (kycApproved) {
    return (
      <div className="mx-auto max-w-lg space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Identity Verification</h1>
          <p className="text-sm text-muted-foreground">Your KYC is complete.</p>
        </div>
        <Card className="border-success/30 bg-success/5">
          <CardContent className="flex flex-col items-center gap-4 py-10">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
              <CheckCircle className="h-8 w-8 text-success" />
            </div>
            <div className="text-center space-y-1">
              <h2 className="text-lg font-semibold text-foreground">KYC approved</h2>
              <p className="text-sm text-muted-foreground">
                Your identity has been verified. You have full borrowing access.
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              Go to Borrow
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Pending review
  if (kycStatus === "pending") {
    return (
      <div className="mx-auto max-w-lg space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Identity Verification</h1>
          <p className="text-sm text-muted-foreground">Your documents are being reviewed.</p>
        </div>
        <Card className="border-warning/30 bg-warning/5">
          <CardContent className="flex flex-col items-center gap-4 py-10">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-warning/10">
              <Clock className="h-8 w-8 text-warning" />
            </div>
            <div className="text-center space-y-1">
              <h2 className="text-lg font-semibold text-foreground">Under review</h2>
              <p className="text-sm text-muted-foreground">
                Your KYC documents have been submitted and are being reviewed. This usually takes less than 24 hours.
              </p>
            </div>
            <Badge className="bg-warning/10 text-warning">Review in progress</Badge>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Submit / resubmit form
  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Identity Verification</h1>
        <p className="text-sm text-muted-foreground">
          {kycStatus === "rejected"
            ? "Your previous submission was rejected. Please resubmit with valid documents."
            : "Submit your identity document to unlock borrowing access. Takes under 2 minutes."}
        </p>
      </div>

      {kycStatus === "rejected" && (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="flex items-center gap-3 py-4">
            <XCircle className="h-5 w-5 text-destructive shrink-0" />
            <p className="text-sm text-foreground">
              Your previous KYC was rejected. Upload a clearer document to try again.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-5">
          <Card className="border border-border/40 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Document type</CardTitle>
              <CardDescription>Choose the identity document you want to submit.</CardDescription>
            </CardHeader>
            <CardContent>
              <Select value={documentType} onValueChange={setDocumentType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="national_id">National ID (NRC)</SelectItem>
                  <SelectItem value="passport">Passport</SelectItem>
                  <SelectItem value="drivers_license">Driver's license</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          <Card className="border border-border/40 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Upload files</CardTitle>
              <CardDescription>Front image is required. Add back if the document has info on both sides.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Front image *</Label>
                <Input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setFrontFile(e.target.files?.[0] || null)}
                />
                {frontFile && (
                  <p className="text-xs text-muted-foreground">Selected: {frontFile.name}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Back image (optional)</Label>
                <Input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setBackFile(e.target.files?.[0] || null)}
                />
                {backFile && (
                  <p className="text-xs text-muted-foreground">Selected: {backFile.name}</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Button
            className="w-full"
            size="lg"
            onClick={handleSubmit}
            disabled={isSubmitting || !documentType || !frontFile}
          >
            <FileBadge2 className="mr-2 h-4 w-4" />
            {isSubmitting ? "Submitting..." : "Submit KYC"}
          </Button>
        </div>

        <div className="space-y-5 lg:sticky lg:top-6">
          <Card className="border border-border/40 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Tips for approval</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                "Document edges should be fully visible",
                "Text and photo must be readable",
                "Upload the latest valid document, not expired",
              ].map((tip, i) => (
                <div key={tip} className="flex gap-3">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                    {i + 1}
                  </div>
                  <p className="text-sm text-muted-foreground">{tip}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border border-border/40 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">What happens next</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <p>Your documents are reviewed by the compliance team.</p>
              <p>Approval unlocks your borrowing access.</p>
              <p>Usually takes less than 24 hours.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
